import React, { useEffect, useState } from 'react';
import { api } from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Info, AlertTriangle, XCircle, CheckCircle, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function TypeIcon({ type }) {
  switch (type) {
    case 'SUCCESS': return <CheckCircle className="w-5 h-5 text-green-600" />;
    case 'WARNING': return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
    case 'ERROR': return <XCircle className="w-5 h-5 text-red-600" />;
    case 'ACTION_REQUIRED': return <AlertTriangle className="w-5 h-5 text-orange-600" />;
    default: return <Info className="w-5 h-5 text-muted-foreground" />;
  }
}

const NotificationsArchive = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    load();
  }, [isAuthenticated, limit, offset]);

  const load = async () => {
    try {
      const res = await api.notificationsArchived(limit, offset);
      const arr = res?.items || [];
      setItems(arr);
      setHasMore(arr.length === limit);
    } catch (e) {
      console.error('Load archived failed', e);
    }
  };

  const openDetail = async (id) => {
    try {
      const n = await api.notificationGet(id);
      setDetail(n);
      setOpen(true);
    } catch (e) {
      console.error('Load detail failed', e);
    }
  };

  const Item = ({ n }) => (
    <button
      onClick={() => openDetail(n.id)}
      className="w-full text-left p-3 border rounded hover:bg-muted transition flex gap-3"
    >
      <div className="mt-0.5"><TypeIcon type={n.type} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="font-medium text-sm text-foreground line-clamp-1">{n.title}</div>
          <div className="text-xs text-muted-foreground">{new Date(n.archivedAt || n.createdAt).toLocaleString('de-DE')}</div>
        </div>
        <div className="text-sm text-muted-foreground line-clamp-2">{n.message}</div>
        {n.actionUrl && n.actionLabel && (
          <div className="mt-1">
            <a href={n.actionUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1">
              {n.actionLabel}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>
    </button>
  );

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-4 border-b">
          <div className="font-semibold">{t('notifications.archive.title')}</div>
        </div>
        <div className="p-4">
          <div className="space-y-2">
            {items.map(n => <Item key={n.id} n={n} />)}
            {!items.length && (
              <div className="text-sm text-muted-foreground">{t('notifications.archive.none')}</div>
            )}
          </div>
          <div className="flex items-center justify-between mt-3">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>{t('notifications.archive.prev')}</Button>
            <div className="text-xs text-muted-foreground">{t('notifications.archive.page', { page: Math.floor(offset / limit) + 1 })}</div>
            <Button variant="outline" size="sm" disabled={!hasMore} onClick={() => setOffset(offset + limit)}>{t('notifications.archive.next')}</Button>
          </div>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          {detail ? (
            <>
              <DialogHeader>
                <DialogTitle>{detail.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  {new Date(detail.createdAt).toLocaleString('de-DE')}
                  {detail.senderName ? ` – Absender: ${detail.senderName}` : ''}
                </div>
                <div className="whitespace-pre-wrap text-sm text-foreground">{detail.message}</div>
                {detail.actionUrl && detail.actionLabel && (
                  <div>
                    <a href={detail.actionUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1">
                      {detail.actionLabel}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsArchive;